import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import FooterAlt from "@/components/FooterAlt";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TrendingUp, CheckCircle2, ArrowRight } from "lucide-react";
import StructuredData from "@/components/StructuredData";

const CaseStudies = () => {
  useEffect(() => {
    document.title = "Portfolio Case Studies: 76-82% CAC Reduction Results | AMX Ventures";
    const desc = "Real success stories: Onepane.ai achieved 82% CAC reduction, Actualize 76% via hyperscaler partnerships. See how ecosystem-led growth drives measurable results.";
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", window.location.href);
  }, []);

  const caseStudiesSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "AMX Ventures Case Studies",
    "description": "Success stories from portfolio companies scaling through ecosystem-led growth",
    "publisher": {
      "@type": "Organization",
      "name": "AMX Ventures"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <StructuredData data={caseStudiesSchema} />
      <Navigation />

      <main className="container mx-auto px-4 py-16">
        <header className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
            Case Studies
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Real results from portfolio companies that scaled through ecosystem-led growth and hyperscaler partnerships.
          </p>
        </header>

        {/* Onepane.ai Case Study */}
        <section className="max-w-6xl mx-auto mb-20">
          <Card className="border-2 hover:shadow-xl transition-shadow">
            <CardHeader className="bg-secondary border-b-2 border-border">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-3xl mb-2">Onepane.ai</CardTitle>
                  <p className="text-muted-foreground">Cloud Resilience & AI-Driven Operations</p>
                </div>
                <Badge className="self-start md:self-auto">Seed Stage</Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold mb-4">The Challenge</h3>
                  <p className="text-muted-foreground mb-6">
                    Onepane entered a crowded cloud operations market facing long sales cycles (9+ months), 
                    high CAC ($180K per enterprise customer), and difficulty differentiating from established competitors.
                  </p>
                  
                  <h3 className="text-2xl font-bold mb-4">The Solution</h3>
                  <p className="text-muted-foreground mb-4">
                    AMX Ventures helped Onepane implement a dual-hyperscaler strategy:
                  </p>
                  <ul className="space-y-2 text-muted-foreground mb-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span>Built deep AWS and Microsoft Azure integrations for cloud resilience monitoring</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span>Listed on AWS Marketplace with optimized private offers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span>Achieved AWS ISV Accelerate status for co-sell access</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span>Connected with AWS and Microsoft GTM teams through AMX network</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span>Positioned as ecosystem-ready solution for enterprise transformation</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-6">The Results</h3>
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <Card className="bg-accent/10 border-accent">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-5 h-5 text-accent" />
                          <span className="text-sm font-semibold text-muted-foreground">CAC Reduction</span>
                        </div>
                        <div className="text-4xl font-bold text-accent">82%</div>
                        <p className="text-sm text-muted-foreground mt-1">$180K → $32K</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-accent/10 border-accent">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-5 h-5 text-accent" />
                          <span className="text-sm font-semibold text-muted-foreground">Sales Cycle</span>
                        </div>
                        <div className="text-4xl font-bold text-accent">4.5x</div>
                        <p className="text-sm text-muted-foreground mt-1">Faster velocity</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-accent/10 border-accent">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-5 h-5 text-accent" />
                          <span className="text-sm font-semibold text-muted-foreground">Win Rate</span>
                        </div>
                        <div className="text-4xl font-bold text-accent">55%</div>
                        <p className="text-sm text-muted-foreground mt-1">vs 20% industry avg</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-accent/10 border-accent">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-5 h-5 text-accent" />
                          <span className="text-sm font-semibold text-muted-foreground">Deal Size</span>
                        </div>
                        <div className="text-4xl font-bold text-accent">2.3x</div>
                        <p className="text-sm text-muted-foreground mt-1">Larger contracts</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="bg-secondary p-6 rounded-lg border-l-4 border-accent">
                    <p className="text-sm font-semibold mb-2 text-foreground">Founder Perspective</p>
                    <p className="text-muted-foreground italic">
                      "AMX didn't just invest capital—they opened doors that would have taken us years to access on our own. 
                      The AWS partnership alone cut our enterprise sales cycle by more than half."
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-border">
                <Button asChild variant="outline">
                  <Link to="/portfolio/onepane">
                    Learn More About Onepane.ai
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Actualize Case Study */}
        <section className="max-w-6xl mx-auto mb-20">
          <Card className="border-2 hover:shadow-xl transition-shadow">
            <CardHeader className="bg-secondary border-b-2 border-border">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-3xl mb-2">Actualize</CardTitle>
                  <p className="text-muted-foreground">AI-Powered Data Intelligence</p>
                </div>
                <Badge className="self-start md:self-auto">Seed Stage</Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold mb-4">The Challenge</h3>
                  <p className="text-muted-foreground mb-6">
                    Actualize entered the competitive data analytics market with innovative AI capabilities but struggled 
                    to gain enterprise trust. Long proof-of-concept cycles and high customer acquisition costs threatened growth.
                  </p>
                  
                  <h3 className="text-2xl font-bold mb-4">The Solution</h3>
                  <p className="text-muted-foreground mb-4">
                    AMX positioned Actualize as a Google Cloud native solution:
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span>Built native integrations with BigQuery and Vertex AI for seamless data workflows</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span>Listed on Google Cloud Marketplace with consumption-based pricing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span>Achieved Partner Advantage certification for co-sell eligibility</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span>Connected with Google Cloud GTM teams through AMX relationships</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span>Positioned as AI-ready solution for data-driven enterprises</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-6">The Results</h3>
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <Card className="bg-accent/10 border-accent">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-5 h-5 text-accent" />
                          <span className="text-sm font-semibold text-muted-foreground">CAC Reduction</span>
                        </div>
                        <div className="text-4xl font-bold text-accent">76%</div>
                        <p className="text-sm text-muted-foreground mt-1">Lower acquisition cost</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-accent/10 border-accent">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-5 h-5 text-accent" />
                          <span className="text-sm font-semibold text-muted-foreground">POC to Close</span>
                        </div>
                        <div className="text-4xl font-bold text-accent">3.5x</div>
                        <p className="text-sm text-muted-foreground mt-1">Faster conversion</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-accent/10 border-accent">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-5 h-5 text-accent" />
                          <span className="text-sm font-semibold text-muted-foreground">Enterprise Wins</span>
                        </div>
                        <div className="text-4xl font-bold text-accent">65%</div>
                        <p className="text-sm text-muted-foreground mt-1">Via GCP co-sell</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-accent/10 border-accent">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-5 h-5 text-accent" />
                          <span className="text-sm font-semibold text-muted-foreground">Deal Size</span>
                        </div>
                        <div className="text-4xl font-bold text-accent">2.8x</div>
                        <p className="text-sm text-muted-foreground mt-1">Larger ACV</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="bg-secondary p-6 rounded-lg border-l-4 border-accent">
                    <p className="text-sm font-semibold mb-2 text-foreground">Key Insight</p>
                    <p className="text-muted-foreground">
                      Enterprises heavily invested in Google Cloud's data ecosystem were actively seeking AI-powered analytics. 
                      Actualize's native BigQuery integration made adoption seamless, turning technical fit into competitive advantage.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-border">
                <Button asChild variant="outline">
                  <Link to="/portfolio/actualize">
                    Learn More About Actualize
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Call to Action */}
        <section className="max-w-4xl mx-auto text-center bg-secondary border-2 border-border rounded-lg p-12">
          <TrendingUp className="w-16 h-16 text-accent mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Scale Through Ecosystem Partnerships?</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            These results aren't outliers—they're what happens when you combine great products with strategic hyperscaler partnerships.
          </p>
          <Button size="lg" asChild className="hover:scale-105 transition-transform font-bold">
            <Link to="/schedule">
              Schedule a Meeting
              <ArrowRight className="ml-2" />
            </Link>
          </Button>
        </section>
      </main>

      <FooterAlt />
    </div>
  );
};

export default CaseStudies;
